import { useState, useRef, useEffect } from 'react'
import { Container, Hero, Button } from '@/components'
import styles from './Contact.module.css'
import { CheckCircle, Mail, MapPin, Phone, XCircle, ChevronDown } from 'lucide-react'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const serviceOptions = [
    { value: '', label: 'Select a service' },
    { value: 'driveway', label: 'Concrete Driveway' },
    { value: 'patio', label: 'Pool Deck, Porch, or Patio' },
    { value: 'sidewalk', label: 'Sidewalk or Walkway' },
    { value: 'slab', label: 'Garage Slab or Pad' },
    { value: 'custom', label: 'Custom Concrete Work' },
    { value: 'demolition', label: 'Demolition' },
    { value: 'other', label: 'Other' }
  ]

  // Close select when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleServiceSelect = (value: string) => {
    setFormData(prev => ({
      ...prev,
      service: value
    }))
    setIsSelectOpen(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Using PHP endpoint for GoDaddy hosting
      const response = await fetch('/contact-form.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      // Check if response is JSON
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        // If not JSON, get the text response for debugging
        const textResponse = await response.text()
        // eslint-disable-next-line no-console
        console.error('Non-JSON response:', textResponse)
        setSubmitStatus('error')
        return
      }

      const result = await response.json()

      if (response.ok && result.success) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        })
      } else {
        setSubmitStatus('error')
        // eslint-disable-next-line no-console
        console.error('Form submission error:', result.error)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Form submission error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* Hero Section */}
      <Hero
        title="Contact H&W Concrete"
        subtitle="Get in touch with us for a free consultation and quote."
        backgroundImage="./img/hero4.png"
      >
        <Button href="/gallery" variant="primary" size="lg">
          See Our Projects
        </Button>
        <Button href="tel:+13212080016" variant="secondary" size="lg">
          Call Now
        </Button>
      </Hero>

      {/* Contact Section */}
      <section className={styles.contactSection}>
        <Container>
          <div className={styles.contactContent}>
            <div className={styles.contactInfo}>
              <h2 className={styles.contactTitle}>Get In Touch</h2>
              <p className={styles.contactDescription}>
                Ready to start your concrete project? Contact H&W Concrete today for a free consultation and quote. 
                We serve all of Brevard County, Florida and surrounding areas.
              </p>
              
              <div className={styles.contactMethods}>
                <div className={styles.contactMethod}>
                  <div className={styles.contactIcon}>
                    <Phone size={24} color="var(--link-color)" />
                  </div>
                  <div className={styles.contactDetails}>
                    <h4>Phone</h4>
                    <a href="tel:+13212080016">(321) 208-0016</a>
                  </div>
                </div>
                
                <div className={styles.contactMethod}>
                  <div className={styles.contactIcon}>
                    <Mail size={24} color="var(--link-color)" />
                  </div>
                  <div className={styles.contactDetails}>
                    <h4>Email</h4>
                    <a href="mailto:patrick@hwconcrete.com">patrick@hwconcrete.com</a>
                  </div>
                </div>
                
                <div className={styles.contactMethod}>
                  <div className={styles.contactIcon}>
                    <MapPin size={24} color="var(--link-color)" />
                  </div>
                  <div className={styles.contactDetails}>
                    <h4>Service Area</h4>
                    <p>Brevard County, Florida</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.contactForm}>
              <h3 className={styles.formTitle}>Request a Free Quote</h3>
              
              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className={styles.successMessage}>
                  <CheckCircle size={24} color="var(--green-9)" />
                  <h4>Thank you for your message!</h4>
                  <p>We&apos;ve received your inquiry and will get back to you within 24 hours. You should also receive a confirmation email shortly.</p>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className={styles.errorMessage}>
                  <XCircle size={24} color="var(--red-9)" />
                  <h4>Something went wrong</h4>
                  <p>Please try again or contact us directly at (321) 208-0016.</p>
                </div>
              )}

              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.formLabel}>Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required 
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.formLabel}>Email *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required 
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="phone" className={styles.formLabel}>Phone</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="service" className={styles.formLabel}>Service Type</label>
                  <div className={styles.customSelect} ref={selectRef}>
                    <button
                      type="button"
                      className={styles.selectButton}
                      onClick={() => setIsSelectOpen(!isSelectOpen)}
                      aria-haspopup="listbox"
                      aria-expanded={isSelectOpen}
                    >
                      <span className={styles.selectValue}>
                        {serviceOptions.find(option => option.value === formData.service)?.label || 'Select a service'}
                      </span>
                      <ChevronDown 
                        size={20} 
                        className={`${styles.selectIcon} ${isSelectOpen ? styles.selectIconOpen : ''}`}
                      />
                    </button>
                    {isSelectOpen && (
                      <div className={styles.selectDropdown}>
                        {serviceOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`${styles.selectOption} ${formData.service === option.value ? styles.selectOptionActive : ''}`}
                            onClick={() => handleServiceSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="message" className={styles.formLabel}>Project Details *</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className={styles.formTextarea}
                    placeholder="Please describe your project, including size, location, and any specific requirements..."
                    required
                  ></textarea>
                </div>
                
                <Button 
                  type="submit" 
                  variant="primary" 
                  size="lg" 
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}

export default Contact
